import { supabase } from '@/shared/lib/supabase';
import { isProjectStatus } from '@/shared/constants/statuses';
import { toDateInputValue } from '@/shared/lib/dates';
import type {
  Coordination,
  Id,
  MinistryWithCoordination,
  MinistryWithLeader,
  ProjectStatusCount,
  ScopeBinding,
} from '@/shared/types/domain';

/**
 * Única camada da feature `org` que conhece o supabase-js (architecture.md
 * §5.1). Nenhum hook, componente ou página importa o client.
 *
 * **Nada aqui filtra por papel, e isso é proposital.** Quem decide o que cada
 * consulta devolve é a RLS: `coordinations` só entrega o que está em
 * `my_coordination_ids()`, `projects` só o que está em `my_ministry_ids()`.
 * Uma checagem de papel aqui seria, na melhor hipótese, redundante; na pior,
 * uma segunda regra de autorização divergindo da do banco.
 */

/** Coordenações ao alcance da pessoa (US-09). */
export async function fetchCoordinations(): Promise<Coordination[]> {
  const { data, error } = await supabase
    .from('coordinations')
    .select('*')
    .order('name');
  if (error) throw error;
  return data;
}

export async function fetchCoordination(id: Id): Promise<Coordination | null> {
  const { data, error } = await supabase
    .from('coordinations')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Ministérios com o líder vigente. Vem da RPC, não de um `select` com join:
 * `can_read_profile` não deixa um coordenador ler a linha do perfil do líder,
 * então o nome só chega por projeção reduzida (spec §5.4).
 *
 * Sem argumento, devolve todos os ministérios ao alcance da pessoa — é o que
 * a aba Coordenações consome numa chamada só, em vez de uma por coordenação.
 */
export async function fetchMinistriesWithLeaders(
  coordinationId?: Id,
): Promise<MinistryWithLeader[]> {
  const { data, error } = await supabase.rpc('list_ministries_with_leaders', {
    p_coordination_id: coordinationId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Os vínculos VIGENTES da pessoa, com nome — o que a seção "Minha área" da
 * sidebar consome (ADR-012 decisão 4).
 *
 * **Por que não `currentUser.ministry_ids`**, que já está em mãos e sairia de
 * graça: aqueles arrays vêm de `my_ministry_ids()`, que devolve ALCANCE, não
 * vínculo — para um overseer, a filial inteira; para o admin, a igreja toda. A
 * sidebar do overseer nasceria com dez itens "Minha área", cada um de um
 * ministério que não é dele. É a mesma armadilha que `fetchMyDirectMinistryIds`
 * documenta em `features/calendar/api/calendar.api.ts`, e a saída é a mesma:
 * ler a linha de `memberships`.
 *
 * ⚠️ **O `.eq('profile_id')` carrega peso e não é decoração.** A política
 * `memberships_select` é `profile_id = current_profile_id() OR is_admin()` —
 * sem o filtro, o admin receberia os vínculos da igreja inteira e a barra dele
 * viraria um diretório de pessoas.
 *
 * O nome vem de join, e aqui isso é seguro ao contrário do caso do líder
 * (`fetchMinistriesWithLeaders`): `ministries` e `coordinations` são legíveis
 * por quem tem vínculo nelas; quem não é legível é `profiles`.
 */
export async function fetchMyScopeBindings(profileId: Id): Promise<ScopeBinding[]> {
  // Mesmo recorte de vigência de `fetchMyDirectMinistryIds`, pelo mesmo helper:
  // "hoje" é o de São Paulo, nunca o do navegador (regra 9).
  const today = toDateInputValue(new Date());

  const { data, error } = await supabase
    .from('memberships')
    .select('coordination_id, ministry_id, coordinations(name), ministries(name)')
    .eq('profile_id', profileId)
    .lte('starts_on', today)
    .or(`ends_on.is.null,ends_on.gte.${today}`);
  if (error) throw error;

  // O genérico existe porque o `flatMap` inferiria o tipo do PRIMEIRO retorno
  // e recusaria o segundo — as duas formas são a mesma união, `ScopeBinding`.
  return (data ?? []).flatMap<ScopeBinding>((row) => {
    // Ministério primeiro: o CHECK do banco garante que só um dos dois está
    // preenchido, e a ordem só existe para o dia em que ele afrouxar.
    if (row.ministry_id && row.ministries) {
      return [{ kind: 'ministry' as const, id: row.ministry_id, name: row.ministries.name }];
    }
    if (row.coordination_id && row.coordinations) {
      return [
        { kind: 'coordination' as const, id: row.coordination_id, name: row.coordinations.name },
      ];
    }
    // Vínculo de filial cai aqui, e é o comportamento pedido. Também cai o
    // vínculo cujo nome a RLS não entregou — item de navegação sem rótulo é
    // pior que item nenhum.
    return [];
  });
}

/** Ministério + nome da coordenação, para o cabeçalho e a trilha. */
export async function fetchMinistry(id: Id): Promise<MinistryWithCoordination | null> {
  const { data, error } = await supabase
    .from('ministries')
    .select('*, coordinations!inner(name)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const { coordinations, ...ministry } = data;
  return { ...ministry, coordination_name: coordinations.name };
}

/**
 * Quantas PESSOAS respondem por uma coordenação ou ministério hoje, dentro do
 * alcance de quem pergunta (US-09).
 *
 * Vem de RPC, e não de um `select` com `count` sobre `memberships`, porque a
 * política daquela tabela é `profile_id = current_profile_id() or is_admin()`:
 * o vínculo das outras pessoas simplesmente não chega ao cliente. A função
 * devolve o inteiro e nada mais — sem nome, sem id, sem papel.
 */
export async function fetchActiveLeaderCount(): Promise<number> {
  const { data, error } = await supabase.rpc('count_active_leaders');
  if (error) throw error;
  return data ?? 0;
}

/**
 * Contagem de projetos por situação, em todo o escopo da pessoa (US-10,
 * US-11). Uma consulta só; as telas agregam por ministério ou por coordenação
 * a partir daqui.
 *
 * Vem da view agregada e nunca de `array.length` sobre uma listagem: a
 * listagem é paginada e filtrada, e um contador que discorda da lista ao lado
 * é pior que contador nenhum.
 *
 * A estreitagem abaixo existe porque o Postgres não consegue provar que
 * coluna de view é não-nula, e o gerador de tipos marca todas como
 * anuláveis. Na prática nenhuma delas é: são as colunas do `group by`, mais
 * um `count(*)`. Linha fora do formato é descartada em vez de virar `NaN` na
 * tela.
 */
export async function fetchProjectStatusCounts(): Promise<ProjectStatusCount[]> {
  const { data, error } = await supabase
    .from('project_status_counts')
    .select('coordination_id, ministry_id, status, total');
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    if (
      row.coordination_id === null ||
      row.ministry_id === null ||
      row.status === null ||
      row.total === null ||
      !isProjectStatus(row.status)
    ) {
      return [];
    }
    return [
      {
        coordination_id: row.coordination_id,
        ministry_id: row.ministry_id,
        status: row.status,
        total: row.total,
      },
    ];
  });
}
